/**
 * The model's sentence or two about the specific week, riding under the
 * scripted commentary on the Wrapped stage.
 *
 * The scripted paragraph is the spine and renders always; this is the bonus
 * that only exists when the server, the key and the quota all cooperate on a
 * Sunday evening. It is fetched once per week per account and cached in
 * Dexie meta, because Wrapped can be reopened from the archive all week and
 * the week's facts do not change after it ends — the same week should read
 * the same way twice. Failures cache nothing and show nothing: an offline
 * Sunday loses only the sentence nobody was promised.
 */

import { useEffect, useState } from 'react'
import { api, hasSession } from '../api/client.js'
import { getMeta, setMeta } from '../db/dexie.js'
import { currentProfile } from '../screens/useProfile.js'
import type { WrappedWeek } from './compute.js'

function metaKey(weekStart: string): string {
  return `wrapped.note.${currentProfile()?.id ?? 'anon'}.${weekStart}`
}

/** The week, as short fact lines for the prompt. */
function factLines(week: WrappedWeek): string[] {
  const facts: string[] = [
    `Savaitė ${week.weekStart} – ${week.weekEnd}.`,
    `Įrašytos ${week.daysLogged} dienos iš 7.`,
  ]
  if (week.daysLogged > 0) facts.push(`Vidurkis ${week.avgKcal} kcal per dieną.`)
  if (week.kcalTarget != null) facts.push(`Tikslas ${week.kcalTarget} kcal.`)
  if (week.gapKcal != null) {
    facts.push(
      week.gapKcal > 0
        ? `Viršyta vidutiniškai ${week.gapKcal} kcal.`
        : `Iki tikslo trūko ${Math.abs(week.gapKcal)} kcal.`,
    )
  }
  if (week.topFood != null) {
    facts.push(`Dažniausias maistas: ${week.topFood.name} (${week.topFood.count} k.).`)
  }
  if (week.weightChangeKg != null) {
    facts.push(
      `Svorio pokytis per savaitę: ${week.weightChangeKg > 0 ? '+' : ''}${week.weightChangeKg} kg.`,
    )
  }
  if (week.workoutCount > 0) facts.push(`Treniruotės: ${week.workoutCount}.`)
  if (week.longestRun > 1) facts.push(`Ilgiausia įrašų serija: ${week.longestRun} d.`)
  if (week.protein.targetG != null) {
    facts.push(`Baltymų tikslas pasiektas ${week.protein.daysMet} d.`)
  }
  return facts.slice(0, 12).map((line) => line.slice(0, 160))
}

/** The cached-or-fetched note for this week, or null while there isn't one. */
export function useWrappedNote(week: WrappedWeek | undefined): string | null {
  const [note, setNote] = useState<string | null>(null)
  const weekStart = week?.weekStart ?? null

  useEffect(() => {
    setNote(null)
    if (week == null || weekStart == null) return
    let cancelled = false

    void (async () => {
      const cached = await getMeta<string | null>(metaKey(weekStart), null)
      if (cancelled) return
      if (cached != null && cached.length > 0) {
        setNote(cached)
        return
      }
      if (!hasSession() || typeof navigator === 'undefined' || !navigator.onLine) return
      try {
        const wire = await api.post<{ note: string }>('/api/ai/wrapped-note', {
          weekStart,
          facts: factLines(week),
        })
        if (cancelled || !wire.note) return
        setNote(wire.note)
        await setMeta(metaKey(weekStart), wire.note)
      } catch {
        // No note this time. The scripted commentary was always the slide.
      }
    })()

    return () => {
      cancelled = true
    }
    // The facts are a pure function of the week, which is keyed by its start.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekStart, week])

  return note
}
